// Types now live in core-fe-lib so the generic config pages can reference them
// without taking a hub dep. Re-exported here so existing imports keep working.
export type {
  ConfigFieldType,
  LLMCapability,
  TenantConfigSchemaEntry,
} from "core-fe-lib/components-shadcn/composables/configSchemaTypes";

import type { TenantConfigSchemaEntry } from "core-fe-lib/components-shadcn/composables/configSchemaTypes";

export const TENANT_CONFIG_SCHEMA: TenantConfigSchemaEntry[] = [
  // ── Features ──────────────────────────────────────────────────────────────
  {
    key: "feature.care.enabled",
    label: "Care Module",
    description: "Enable the care management module.",
    type: "boolean",
    defaultValue: "true",
    category: "Features",
  },
  {
    key: "feature.recruitment.enabled",
    label: "Recruitment Module",
    description: "Enable the recruitment module.",
    type: "boolean",
    defaultValue: "false",
    category: "Features",
  },
  {
    key: "feature.social.enabled",
    label: "Social Features",
    description: "Enable social features (feed, reactions, comments).",
    type: "boolean",
    defaultValue: "false",
    category: "Features",
  },
  {
    key: "feature.document.enabled",
    label: "Document Module",
    description: "Enable document management module.",
    type: "boolean",
    defaultValue: "true",
    category: "Features",
  },
  {
    key: "feature.scholar.enabled",
    label: "Scholar Module",
    description: "Enable the scholar AI evaluation module.",
    type: "boolean",
    defaultValue: "false",
    category: "Features",
  },
  {
    key: "feature.teamwork.enabled",
    label: "Teamwork Module",
    description: "Enable the teamwork module.",
    type: "boolean",
    defaultValue: "false",
    category: "Features",
  },

  // ── UI ────────────────────────────────────────────────────────────────────
  {
    key: "ui.language.default",
    label: "Default Language",
    description: "Default interface language for new users.",
    type: "enum",
    defaultValue: "en-US",
    options: [
      { value: "en-US", label: "English" },
      { value: "fr", label: "Français" },
    ],
    category: "UI",
  },
  {
    key: "ui.theme",
    label: "Default Theme",
    description: "Default UI color theme.",
    type: "enum",
    defaultValue: "light",
    options: [
      { value: "light", label: "Light" },
      { value: "dark", label: "Dark" },
      { value: "system", label: "System" },
    ],
    category: "UI",
  },
  {
    key: "ui.pagination.default_size",
    label: "Default Page Size",
    description: "Default number of items per page in lists.",
    type: "number",
    defaultValue: "25",
    category: "UI",
  },

  // ── Authentication ─────────────────────────────────────────────────────────
  {
    key: "auth.session_timeout_minutes",
    label: "Session Timeout (minutes)",
    description: "Session timeout in minutes. Set to 0 for no expiry.",
    type: "number",
    defaultValue: "60",
    category: "Authentication",
  },
  {
    key: "auth.mfa.required",
    label: "Require MFA",
    description: "Require multi-factor authentication for all users.",
    type: "boolean",
    defaultValue: "false",
    category: "Authentication",
  },

  // ── Notifications ──────────────────────────────────────────────────────────
  {
    key: "notification.email.enabled",
    label: "Email Notifications",
    description: "Enable outgoing email notifications.",
    type: "boolean",
    defaultValue: "true",
    category: "Notifications",
  },
  {
    key: "notification.email.sender",
    label: "Sender Email Address",
    description: "Sender email address for outgoing notifications.",
    type: "text",
    category: "Notifications",
  },
  {
    key: "notification.email.sender_name",
    label: "Sender Display Name",
    description: "Display name used for outgoing notification emails.",
    type: "text",
    category: "Notifications",
  },

  // ── Scholar ───────────────────────────────────────────────────────────────
  // LLM keys are dynamic: dropdowns are rendered from `pipe_llm_pricing`
  // rows tagged with the matching capability. To register a new model
  // option just seed a registry entry — no schema edit needed.
  {
    key: "scholar.copy.transcription.llm",
    label: "Copy Transcription LLM",
    description:
      "Vision model that transcribes a student's handwritten copy into text. Per-copy hot path — this is the main cost lever for the OCR pipeline. Default: OVH Qwen2.5-VL.",
    type: "llm",
    requiresCapability: "vision",
    taskType: "scholar_ocr",
    defaultValue: "ovh/Qwen2.5-VL-72B-Instruct",
    category: "Scholar",
  },
  {
    key: "scholar.copy.exercise.mapper.llm",
    label: "Exercise Mapper LLM",
    description:
      "Vision model that locates each exercise's bounding box on a multi-section copy image (structured_image sessions). Runs at per-copy setup. Default: OVH Qwen2.5-VL.",
    type: "llm",
    requiresCapability: "vision",
    taskType: "scholar_ocr",
    defaultValue: "ovh/Qwen2.5-VL-72B-Instruct",
    category: "Scholar",
  },
  {
    key: "scholar.sujet.parser.llm",
    label: "Sujet Parser LLM",
    description:
      "Vision model that reads a printed sujet (exam paper) image and extracts the exercise tree, sub-questions and max scores. Runs once per session at setup. Default: OVH Qwen2.5-VL.",
    type: "llm",
    requiresCapability: "vision",
    taskType: "scholar_ocr",
    defaultValue: "ovh/Qwen2.5-VL-72B-Instruct",
    category: "Scholar",
  },
  {
    key: "scholar.rubric.parser.llm",
    label: "Rubric Parser LLM",
    description:
      "Vision model that reads a printed rubric / evaluation grid image and extracts criteria and max scores. Runs once per evaluator import. Default: OVH Qwen2.5-VL.",
    type: "llm",
    requiresCapability: "vision",
    taskType: "scholar_ocr",
    defaultValue: "ovh/Qwen2.5-VL-72B-Instruct",
    category: "Scholar",
  },
  {
    key: "scholar.copy.segmenter.llm",
    label: "Segmenter LLM",
    description:
      "Model used for text segmentation (splitting OCR output into per-section transcripts). Any capable text model. Default: Google Gemini 2.5 Flash.",
    type: "llm",
    requiresCapability: "text",
    taskType: "scholar_segmentation",
    defaultValue: "googleai/gemini-2.5-flash",
    category: "Scholar",
  },
  {
    key: "scholar.copy.ocr.raw.llm",
    label: "OCR Raw LLM",
    description:
      "First-pass raw OCR for the two-step pipeline (when use_ocr_pipeline is enabled on a session). Hands the raw transcript to OCR_BRAIN_LLM for cross-referencing with the source image. Renamed from OCR_LLM for clarity. Default: Mistral OCR latest.",
    type: "llm",
    requiresCapability: "ocr",
    taskType: "scholar_ocr",
    defaultValue: "mistral/mistral-ocr-latest",
    category: "Scholar",
  },
  {
    key: "scholar.copy.ocr.brain.llm",
    label: "OCR Brain LLM",
    description:
      "Vision model used for brain correction in the two-step OCR pipeline. Cross-references the raw OCR text against the original image to produce a faithful corrected transcription.",
    type: "llm",
    requiresCapability: "vision",
    taskType: "scholar_brain_correction",
    defaultValue: "ovh/Qwen2.5-VL-72B-Instruct",
    category: "Scholar",
  },
  {
    key: "scholar.copy.review.locator.llm",
    label: "Selection Locator LLM",
    description:
      "On-demand vision model. When a reviewer selects a span of transcript text, this model is asked to return the bounding box of that text on the corresponding page image so the popover can show the matching region. Called once per selection — pick a fast Flash-class model. Do NOT pick a fine-tuned layout detector like dots.ocr; the locator prompt expects a single-bbox JSON response that only generic VL models follow reliably. Default: Google Gemini 2.5 Flash.",
    type: "llm",
    requiresCapability: "vision",
    taskType: "scholar_locator",
    defaultValue: "googleai/gemini-2.5-flash",
    category: "Scholar",
  },

  // ── AI ────────────────────────────────────────────────────────────────────
  {
    key: "ai.enabled",
    label: "AI Features",
    description: "Enable AI-assisted features across modules.",
    type: "boolean",
    defaultValue: "false",
    category: "AI",
  },
  {
    key: "ai.model",
    label: "AI Model",
    description: "AI model used for suggestions and automation.",
    type: "enum",
    defaultValue: "claude-haiku-4-5",
    options: [
      { value: "claude-haiku-4-5", label: "Claude Haiku 4.5" },
      { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
      { value: "gpt-4o-mini", label: "GPT-4o Mini" },
      { value: "gpt-4o", label: "GPT-4o" },
    ],
    category: "AI",
  },

  // ── Learning (LMS) ───────────────────────────────────────────────────────
  // Read by backend lms/api/handler.go (PublicCatalogEnabled). When ON, the
  // anonymous /public-api/v1/lms/catalog endpoints serve this tenant's
  // published courses to non-logged-in visitors; OFF → those endpoints 404.
  {
    key: "lms.public_catalog.enabled",
    label: "Public course catalog",
    description:
      "When ON, anonymous visitors can browse this tenant's published courses (at /lms/browse) without logging in; they must still register to enrol. Default: off.",
    type: "boolean",
    defaultValue: "false",
    category: "Learning (LMS)",
  },
  // Read by backend lms/api/handler.go (LivePollsEnabled). When ON, course
  // participants can scan a room QR and vote anonymously (no account); OFF →
  // the /public-api/v1/lms/poll endpoints 404.
  {
    key: "lms.live_polls.enabled",
    label: "Live polls",
    description:
      "When ON, presenters can run live polls during a course: participants scan a QR code and vote anonymously, with results shown live. Default: off.",
    type: "boolean",
    defaultValue: "false",
    category: "Learning (LMS)",
  },
  // Read by backend lms/api/handler.go (AIAuthoringEnabled) and surfaced via
  // GetLmsFeatures.aiAuthoring; gates the in-editor AI course-authoring dock.
  {
    key: "lms.ai_authoring.enabled",
    label: "AI course authoring",
    description:
      "When ON, authors get an in-editor AI assistant that drafts and edits lessons (proposing changes for approval). Default: off.",
    type: "boolean",
    defaultValue: "false",
    category: "Learning (LMS)",
  },
  // Read by backend lms/service (draftModelKey): the model used for instant
  // draft generation AND per-slide remix. Empty → built-in default (gemini-3-flash).
  {
    key: "lms.authoring.llm",
    label: "Authoring model",
    description:
      "Text model used for AI course drafting and per-slide remix. Leave unset to use the built-in default (Gemini 3 Flash).",
    type: "llm",
    requiresCapability: "text",
    defaultValue: "googleai/gemini-3-flash-preview",
    category: "Learning (LMS)",
  },

  // ── AI Employee — Security ───────────────────────────────────────────────
  // Read by backend integration_handler.go (checkTransportPolicy).
  {
    key: "aiemployee.security.allow_stdio",
    label: "Allow stdio MCP servers",
    description:
      "When ON, sub-agents can install MCP tools that run as backend subprocesses. Stdio servers can execute arbitrary commands defined in tenant data — only enable for trusted admins. Default: off.",
    type: "boolean",
    defaultValue: "false",
    category: "AI Employee",
  },
  // Read by backend RetentionSweeper (service/retention_sweeper.go). Daily,
  // deletes terminal runs (and their steps/tool-events/annotations) older than
  // this many days. 0 = keep forever. Expired LangGraph checkpoints are always
  // purged regardless. Never touches the knowledge corpus or credit ledger.
  {
    key: "aiemployee.retention.run_days",
    label: "Run history retention (days)",
    description:
      "How long to keep finished run history (runs, steps, tool events, annotations) before the daily sweeper deletes it. 0 = keep forever. Does not affect the knowledge base, conversations, or billing records.",
    type: "number",
    defaultValue: "0",
    category: "AI Employee",
  },

  // ── AI Employee — Memory ─────────────────────────────────────────────────
  // Read by backend MemoryFactory (service/memory_factory.go). Resolution
  // order: this tenant config → AIEMPLOYEE_* env vars → built-in defaults.
  // The factory splits the registry-style `provider/model` value on the
  // first `/` to recover the provider + model arguments BuildChatModel and
  // BuildEmbedder expect.
  {
    key: "aiemployee.memory.llm",
    label: "Memory & knowledge LLM",
    description:
      "Text-capable chat model used for two ingest-time jobs: (1) extracting durable facts from each chat turn into long-term memory, and (2) writing the per-chunk 'contextual retrieval' blurb when ingesting knowledge-base documents (ADR 016). Default is `mistral/mistral-small-latest` — EU-hosted (data residency: a KB-contextualisation call sees the whole document) and cheaper than gpt-4o-mini; requires MISTRAL_API_KEY. 'private' points at the in-house vLLM box (PRIVATE_AI_MACHINE).",
    type: "llm",
    requiresCapability: "text",
    defaultValue: "mistral/mistral-small-latest",
    category: "AI Employee",
  },
  {
    key: "aiemployee.embedder.llm",
    label: "Embedder LLM",
    description:
      "Embedding model used for semantic recall of stored memories. Value format is `provider/served-name` with exactly one slash. Default is `ovh/bge-m3` — OVH AI Endpoints' managed BGE-M3 (1024-dim native, no truncation). Requires OVH_API_KEY on the server. Alternatives: `private/bge-m3` for a self-hosted TEI / vLLM box (served-name must drop the HuggingFace org prefix — `bge-m3`, not `BAAI/bge-m3` — and align with `--served-model-name` (vLLM) or `--model-id` (TEI)); `openai/text-embedding-3-*` is NOT recommended (not native 1024-dim and the `dimensions` truncation parameter has been unreliable in practice). MUST produce 1024-dim vectors regardless of provider.",
    type: "llm",
    requiresCapability: "embedder",
    defaultValue: "ovh/bge-m3",
    category: "AI Employee",
  },
];

/** O(1) lookup by key */
export const SCHEMA_MAP = new Map<string, TenantConfigSchemaEntry>(
  TENANT_CONFIG_SCHEMA.map((e) => [e.key, e])
);

/** Ordered list of unique category names */
export const SCHEMA_CATEGORIES = [
  ...new Set(TENANT_CONFIG_SCHEMA.map((e) => e.category)),
];

export function getSchemaEntry(
  key: string
): TenantConfigSchemaEntry | undefined {
  return SCHEMA_MAP.get(key);
}
